// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Modal, Text } from '@mantine/core';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { useDisclosure } from '@mantine/hooks';
import { BaseDocumentContextProps, DocumentDto, PositionDto } from '../DocumentT';
import { DocumentS } from '../../Templates/Documents/DocumentS';
import { PositionS } from '../PositionS';
import { PositionGroupS } from '../PositionGroupS';
import { openDeleteConfirmModal } from '../../Atoms/DeleteConfirmModal';
import PositionModal from '../../Molecules/Document/Position/PositionModalV2';
import { openDeletePositionGroupModal } from '../../Molecules/Document/Position/PositionGroupModals';
import { ViolationContext } from './ViolationContext';

interface DocumentContextProps extends BaseDocumentContextProps<DocumentDto> {
  sum: number;
  negativeSum: boolean;
  refresh: () => void;
  transmittingDocument: boolean;
  operationPending: boolean;
  setTransmittingDocument: (val: boolean) => void;
}

export const DocumentContext = createContext<DocumentContextProps>({} as DocumentContextProps);

export const DocumentContextProvider = () => {
  const { documentId } = useParams();
  const [loading, setLoading] = useState(false);
  const [transmittingDocument, setTransmittingDocument] = useState(false);
  const [bookitupDocument, setBookitupDocument] = useState<DocumentDto | null>(null);
  const [selectedPositionId, selectPosition] = useState<number>();
  const [opened, { toggle }] = useDisclosure(false);
  const { interceptViolation } = useContext(ViolationContext);
  const [operationPending, setOperationPending] = useState(false);

  const sum = 0;
  const negativeSum = false;

  useEffect(() => {
    if (documentId) {
      setLoading(true);
      DocumentS.fetchById(Number(documentId))
        .then(setBookitupDocument)
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  const refresh = async () => {
    if (documentId) {
      const resJSON = await DocumentS.fetchById(Number(documentId));
      if (resJSON) {
        setBookitupDocument(resJSON);
      }
    }
  };

  const handleChange = (patch: Partial<DocumentDto>) => {
    setBookitupDocument((prev) => {
      if (prev) {
        return { ...prev, ...patch };
      }
      return null;
    });
  };

  const savePatch = async (patch: Partial<DocumentDto>) => {
    if (documentId) {
      setOperationPending(true);
      const resJSON = await DocumentS.patchDocument(Number(documentId), patch, interceptViolation);
      setOperationPending(false);
      if (resJSON) {
        setBookitupDocument(resJSON);
        return true;
      }
    }
    return false;
  };

  const patchPosition = async (positionId: number, patch: Partial<PositionDto>) => {
    setOperationPending(true);
    const resJSON = await PositionS.patchPosition(positionId, patch);
    setOperationPending(false);
    if (resJSON) {
      // Refresh document to properly update all tax/sum values
      refresh();
      return true;
    }
    return false;
  };

  const movePosition = async (positionId: number, targetPositionId: number) => {
    if (documentId) {
      const resJSON = await DocumentS.movePositionV2(Number(documentId), positionId, targetPositionId);
      if (resJSON) {
        setBookitupDocument(resJSON);
        return true;
      }
    }
    return false;
  };

  const deletePosition = async (positionId: number) => {
    const onConfirm = async () => {
      if (documentId) {
        const resJSON = await DocumentS.deletePositionV2(Number(documentId), positionId);
        if (resJSON) {
          setBookitupDocument(resJSON);
          return true;
        }
      }
      return false;
    };
    openDeleteConfirmModal({
      title: 'Wirklich löschen',
      description: <Text>Möchten Sie diese Position wirklich entfernen?</Text>,
      onConfirm,
    });
  };

  const addPosition = async (targetPositionId?: number, product?: PositionDto) => {
    if (documentId) {
      const resJSON = await DocumentS.insertPositionV2(Number(documentId), targetPositionId, product);
      if (resJSON) {
        setBookitupDocument(resJSON);
        return true;
      }
    }
    return false;
  };

  const addGroup = async (index?: number) => {
    if (documentId) {
      const resJSON = await PositionGroupS.create(Number(documentId), index);
      if (resJSON) {
        handleChange({ positionGroups: resJSON });
        return true;
      }
    }
    return false;
  };

  const moveGroup = async (from: number, to: number) => {
    if (documentId) {
      const resJSON = await PositionGroupS.move(Number(documentId), from, to);
      if (resJSON) {
        setBookitupDocument(resJSON);
        return true;
      }
    }
    return false;
  };

  const insertPositionIntoGroup = async (groupId: string, positionId: number, targetPositionId?: number) => {
    if (documentId) {
      const resJSON = await PositionGroupS.insertPosition(groupId, positionId, Number(documentId), targetPositionId);
      if (resJSON) {
        setBookitupDocument(resJSON);
        return true;
      }
    }
    return false;
  };

  const insertNewPositionIntoGroup = async (groupId: string, position?: PositionDto) => {
    if (documentId) {
      const resJSON = await PositionGroupS.insertNewPosition(groupId, Number(documentId), undefined, position);
      if (resJSON) {
        setBookitupDocument(resJSON);
        return true;
      }
    }
    return false;
  };

  const removePositionFromGroup = async (positionId: number) => {
    if (documentId) {
      const resJSON = await PositionGroupS.deletePosition(positionId, Number(documentId));
      if (resJSON) {
        setBookitupDocument(resJSON);
        return true;
      }
    }
    return false;
  };

  const removeGroup = async (groupId: string, label: string) => {
    if (bookitupDocument) {
      const onConfirm = async (removePositions: boolean) => {
        const { id } = bookitupDocument;
        const resJSON = await PositionGroupS.remove(groupId, id, removePositions);
        if (resJSON) {
          setBookitupDocument(resJSON);
          return true;
        }
        return false;
      };
      openDeletePositionGroupModal({
        label,
        onConfirm,
        positionsPresent: bookitupDocument.positions.length > 0,
      });
    }
  };

  const swapPositions = async (direction: string, positionId: number) => {
    if (documentId) {
      const resJSON = await DocumentS.swapPositions(Number(documentId), direction, positionId);
      if (resJSON) {
        setBookitupDocument(resJSON);
      }
    }
  };

  const openPositionModal = (position: PositionDto) => {
    selectPosition(position.id);
    toggle();
  };

  const addProducts = async (productIds: number[], targetPositionId?: number) => {
    if (documentId) {
      const resJSON = await DocumentS.insertProducts(Number(documentId), productIds, targetPositionId);
      if (resJSON) {
        setBookitupDocument(resJSON);
        return true;
      }
    }
    return false;
  };

  const value = useMemo(
    () => ({
      bookitupDocument,
      loading,
      sum,
      negativeSum,
      operationPending,
      transmittingDocument,
      refresh,
      setTransmittingDocument,
      handleChange,
      savePatch,
      groupHandlers: {
        addGroup,
        moveGroup,
        insertNewPositionIntoGroup,
        removeGroup,
      },
      positionHandlers: {
        openPositionModal,
        patchPosition,
        movePosition,
        deletePosition,
        addPosition,
        swapPositions,
        insertPositionIntoGroup,
        removePositionFromGroup,
        addProducts,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bookitupDocument, transmittingDocument, loading, opened, operationPending, selectedPositionId],
  );

  return (
    <DocumentContext.Provider value={value}>
      {selectedPositionId && (
        <Modal
          closeOnClickOutside={false}
          transition="slide-down"
          opened={opened}
          onClose={() => {
            selectPosition(undefined);
            toggle();
          }}
          title={
            <Text size="xl" weight="bolder">
              Position
            </Text>
          }
        >
          <PositionModal
            bookitupDocument={bookitupDocument}
            position={bookitupDocument?.positions.find(p => p.id === selectedPositionId) as PositionDto}
            savePatch={(patch) => patchPosition(selectedPositionId, patch)}
          />
        </Modal>
      )}
      <Outlet />
    </DocumentContext.Provider>
  );
};
